import * as Label from '@radix-ui/react-label';
import { useId } from 'react';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';

// INV-13 is a build gate, not a later pass. A Radix Label ties the text to the control properly,
// the error is announced rather than only coloured, and the hint is referenced by the input so a
// screen reader reads it — none of which happens if a `<div>` is used as a label.

interface FieldProps extends Omit<ComponentPropsWithoutRef<'input'>, 'id'> {
  readonly label: string;
  readonly hint?: ReactNode;
  readonly error?: string | undefined;
}

export function Field({ label, hint, error, ...input }: FieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ');

  return (
    <div className="flex flex-col gap-1.5">
      <Label.Root htmlFor={id} className="text-xs text-ink-soft">
        {label}
      </Label.Root>
      {hint ? (
        <p id={hintId} className="text-xs text-muted">
          {hint}
        </p>
      ) : null}
      <input
        id={id}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy || undefined}
        className="input"
        {...input}
      />
      {error ? (
        <p id={errorId} className="text-sm font-semibold text-accent-2-700">
          {error}
        </p>
      ) : null}
    </div>
  );
}
